/**
 * Asks the bridge for a fresh keyframe of one received video.
 *
 * A page cannot send a PLI itself. The bridge requests a keyframe from the
 * sender whenever it starts forwarding a source again, because the receiver
 * cannot decode a stream joined mid-picture. So the stall monitor's keyframe
 * step (vrs-layout/remoteVideoStall.ts) drops the stalled source from the
 * receiver constraints for a moment and restores them: the bridge suspends
 * it, resumes it, and asks the sender for a keyframe. The picture was already
 * frozen, so the pause costs nothing visible.
 */
import logger from './logger';
/** How long the source stays out of the constraints. */
export const KEYFRAME_REQUEST_HOLD_MS = 800;
/** One request per source in this window. */
export const KEYFRAME_REQUEST_COOLDOWN_MS = 20000;
let applied;
const lastRequestAt = new Map();
/**
 * Called by subscriber.ts after every receiver constraints update, so a
 * keyframe request restores exactly what the layout asked for.
 *
 * @param {Object} conference - The JitsiConference the constraints went to.
 * @param {Object} constraints - The receiver constraints sent.
 * @returns {void}
 */
export function rememberReceiverConstraints(conference, constraints) {
    if (applied?.conference !== conference) {
        lastRequestAt.clear();
    }
    applied = {
        conference,
        constraints
    };
}
/**
 * Requests a keyframe for one remote source through the bridge.
 *
 * @param {Object} conference - The JitsiConference receiving the source.
 * @param {string} sourceName - The remote video source name.
 * @param {number} now - Current time.
 * @returns {boolean} Whether a request went out.
 */
export function requestRemoteKeyframe(conference, sourceName, now = Date.now()) {
    const current = applied;
    if (!sourceName || !current || current.conference !== conference
        || typeof conference?.setReceiverConstraints !== 'function' || isDirectMedia(conference)) {
        return false;
    }
    const last = lastRequestAt.get(sourceName);
    if (last !== undefined && now - last < KEYFRAME_REQUEST_COOLDOWN_MS) {
        return false;
    }
    const withoutSource = (sources) => {
        if (!Array.isArray(sources)) {
            return sources;
        }
        return sources.filter(source => source !== sourceName);
    };
    const suspended = {
        ...current.constraints,
        constraints: {
            ...current.constraints.constraints,
            [sourceName]: { maxHeight: 0 }
        },
        onStageSources: withoutSource(current.constraints.onStageSources),
        selectedSources: withoutSource(current.constraints.selectedSources)
    };
    try {
        conference.setReceiverConstraints(suspended);
    }
    catch (error) {
        logger.warn('Keyframe request: suspending the source failed.', error);
        return false;
    }
    lastRequestAt.set(sourceName, now);
    setTimeout(() => {
        // Whatever the layout asked for last, even if it changed meanwhile.
        const latest = applied;
        if (!latest || latest.conference !== conference) {
            return;
        }
        try {
            conference.setReceiverConstraints(latest.constraints);
        }
        catch (error) {
            logger.warn('Keyframe request: restoring the constraints failed.', error);
        }
    }, KEYFRAME_REQUEST_HOLD_MS);
    return true;
}
/**
 * Whether a keyframe can be asked for in this conference.
 *
 * @param {Object} conference - The JitsiConference.
 * @returns {boolean}
 */
export function canRequestRemoteKeyframe(conference) {
    return Boolean(applied && applied.conference === conference) && !isDirectMedia(conference);
}
/**
 * On a direct (P2P) connection the constraints go to the other device, which
 * then sends nothing for the source until a restore reaches it; a lost
 * restore would leave the picture black with nothing arriving for the stall
 * guard to notice. The ladder re-attaches instead.
 *
 * @param {Object} conference - The JitsiConference.
 * @returns {boolean}
 */
function isDirectMedia(conference) {
    try {
        return typeof conference?.isP2PActive === 'function' && Boolean(conference.isP2PActive());
    }
    catch {
        return false;
    }
}
/**
 * For tests.
 *
 * @returns {void}
 */
export function resetKeyframeRequestsForTests() {
    applied = undefined;
    lastRequestAt.clear();
}
